// frontend/tracking.js

console.log("VibeSend Tracking Module Loaded..."); // Log module load

// --- Constants (keep outside function) ---
const VIEWER_ID_STORAGE_KEY = 'vibesend_viewer_id';
const VIEWER_EMAIL_STORAGE_KEY = 'vibesend_viewer_email'; // New key for email
const INTERSECTION_THRESHOLD = 0.5; // 50% visible to count as 'view'
const MIN_TIME_ON_PAGE_MS = 1000; // Minimum time (ms) to record time_on_page
const FUNCTION_URL = '/track-event'; // Cloudflare function path

// --- State Variables (keep outside, managed by init) ---
let viewerId = null;
let pageTimers = {};
let currentPageNumber = null;
let currentPageStartTime = null;
let currentDocId = null;
let currentViewMode = null;
let isInitialized = false; // Prevent multiple initializations


// --- Core Initialization Function ---
export function initializeTracking(docId, viewMode) {
    if (isInitialized) {
        console.warn("Tracking already initialized.");
        return;
    }
    if (!docId || !viewMode) {
        console.error("Tracking initialization failed: Missing docId or viewMode.", { docId, viewMode });
        return;
    }

    currentDocId = docId;
    currentViewMode = viewMode;
    viewerId = getViewerId(); // Get or generate viewer ID

    console.log(`VibeSend Tracking Initializing for doc ${currentDocId}, mode ${currentViewMode}, viewer ${viewerId}`);

    // Reset state in case of re-initialization (though currently prevented)
    pageTimers = {};
    currentPageNumber = null;
    currentPageStartTime = null;

    // Initialize based on view mode
    if (currentViewMode === 'scroll') {
        setupScrollTracking();
    } else if (currentViewMode === 'paginate') {
        setupPaginateTracking();
    }

    // Send pending time events on page leave/hide
    setupUnloadTracking();

    isInitialized = true;
    console.log("VibeSend Tracking Initialized Successfully.");
}

// --- Helper Functions (keep inside module scope) ---

function getViewerId() {
    let id = sessionStorage.getItem(VIEWER_ID_STORAGE_KEY);
    if (!id) {
        id = crypto.randomUUID();
        sessionStorage.setItem(VIEWER_ID_STORAGE_KEY, id);
        console.log("Generated new viewer ID:", id);
    }
    return id;
}

// Function to store the verified email
export function setViewerEmail(email) {
    if (email) {
        sessionStorage.setItem(VIEWER_EMAIL_STORAGE_KEY, email);
        console.log("Stored viewer email:", email);
    } else {
        sessionStorage.removeItem(VIEWER_EMAIL_STORAGE_KEY);
        console.log("Cleared stored viewer email.");
    }
}

// Function to retrieve the stored email
function getViewerEmail() {
    return sessionStorage.getItem(VIEWER_EMAIL_STORAGE_KEY);
}

async function sendAnalyticsEvent(eventType, pageNumber, durationMs = null) {
    console.log('Attempting to send event:', eventType, pageNumber, durationMs);
    if (!currentDocId || !viewerId || typeof pageNumber !== 'number') {
        console.warn("Skipping event send, missing data:", { docId: currentDocId, viewerId, pageNumber });
        return;
    }

    const viewerEmail = getViewerEmail(); // Get the email

    const payload = {
        docId: currentDocId,
        viewerId: viewerId,
        eventType: eventType,
        pageNumber: pageNumber,
        email: viewerEmail, // Include email in payload (will be null if not set)
    };
    if (durationMs !== null) {
        payload.durationMs = Math.round(durationMs);
    }

    console.log(`Sending event: ${eventType}`, payload);

    try {
        const isUnloading = eventType === 'time_on_page' && (document.visibilityState === 'hidden');

        if (isUnloading && navigator.sendBeacon) {
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
            const success = navigator.sendBeacon(FUNCTION_URL, blob);
            console.log(`Beacon send ${success ? 'successful' : 'failed'}`);
        } else {
            const response = await fetch(FUNCTION_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
                keepalive: isUnloading
            });

            if (!response.ok) {
                console.error(`Failed to send ${eventType} event:`, response.status, await response.text());
            } else {
                console.log(`${eventType} event sent successfully.`);
            }
        }
    } catch (error) {
        console.error(`Error sending ${eventType} event:`, error);
    }
}

// --- Scroll Mode Specific ---

function setupScrollTracking() {
    const observerOptions = {
        root: null, // Use viewport as root
        rootMargin: '0px',
        threshold: INTERSECTION_THRESHOLD, // Trigger when 50% visible
    };

    const intersectionCallback = (entries) => {
        entries.forEach(entry => {
            const pageElement = entry.target;
            const pageNumber = parseInt(pageElement.dataset.pageNumber, 10);
            console.log('Intersection:', { page: pageNumber, isIntersecting: entry.isIntersecting, ratio: entry.intersectionRatio });
            if (isNaN(pageNumber)) return;

            if (entry.isIntersecting && entry.intersectionRatio >= INTERSECTION_THRESHOLD) {
                // Page became visible
                if (!pageTimers[pageNumber]) { // Only record start time once
                    pageTimers[pageNumber] = Date.now();
                    sendAnalyticsEvent('page_view', pageNumber);
                }
            } else {
                // Page became hidden or less than threshold
                if (pageTimers[pageNumber]) {
                    recordTimeOnPage(pageNumber); // Calculate and send time_on_page
                    delete pageTimers[pageNumber]; // Clear timer
                }
            }
        });
    };

    const observer = new IntersectionObserver(intersectionCallback, observerOptions);

    const intervalId = setInterval(() => {
        const pageElements = document.querySelectorAll('.pdf-page[data-page-number]');
        if (pageElements.length > 0) {
            clearInterval(intervalId);
            console.log(`Observing ${pageElements.length} pages for scroll tracking.`);
            pageElements.forEach(el => observer.observe(el));
        } else {
            console.log("Waiting for .pdf-page elements to appear...");
        }
    }, 500);
}

function recordTimeOnPage(pageNumber) {
    const startTime = pageTimers[pageNumber];
    if (startTime) {
        const durationMs = Date.now() - startTime;
        if (durationMs >= MIN_TIME_ON_PAGE_MS) {
            sendAnalyticsEvent('time_on_page', pageNumber, durationMs);
        } else {
            console.log(`Skipping time_on_page for page ${pageNumber}, duration ${durationMs}ms < ${MIN_TIME_ON_PAGE_MS}ms`);
        }
    }
}

// --- Paginate Mode Specific ---

function setupPaginateTracking() {
    document.addEventListener('vibesend:pagechanged', handlePageChange);

    const initialPage = parseInt(document.body.dataset.currentPage, 10);
    if (!isNaN(initialPage)) {
        handlePageChange({ detail: { newPageNumber: initialPage, oldPageNumber: null } });
    } else {
        console.log("Paginate tracking waiting for initial page event.");
    }
}

function handlePageChange(event) {
    const { newPageNumber, oldPageNumber } = event.detail;
    console.log('Page Change Event Received:', event.detail);
    console.log(`Paginate: Page changed from ${oldPageNumber} to ${newPageNumber}`);

    const now = Date.now();

    if (oldPageNumber !== null && currentPageStartTime !== null) {
        const durationMs = now - currentPageStartTime;
        if (durationMs >= MIN_TIME_ON_PAGE_MS) {
            sendAnalyticsEvent('time_on_page', oldPageNumber, durationMs);
        } else {
             console.log(`Skipping time_on_page for page ${oldPageNumber}, duration ${durationMs}ms < ${MIN_TIME_ON_PAGE_MS}ms`);
        }
    }

    if (newPageNumber !== null) {
        sendAnalyticsEvent('page_view', newPageNumber);
        currentPageNumber = newPageNumber;
        currentPageStartTime = now;
    } else {
        currentPageNumber = null;
        currentPageStartTime = null;
    }
}

// --- Unload/Visibility/Focus Tracking ---

function setupUnloadTracking() {
    // Visibility Change (Tab hidden/shown)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            console.log("Page hidden (visibilitychange), recording final times...");
            recordAllPendingTimes();
        } else if (document.visibilityState === 'visible') {
            console.log("Page visible (visibilitychange), restarting timers if needed...");
            restartTimersOnFocus(); // Use the same logic as window focus
        }
    });

    // Page Hide (Navigating away, closing tab - more reliable for unload)
    window.addEventListener('pagehide', (event) => {
         console.log("Page hide event (pagehide), recording final times...");
         // No need to check event.persisted here for recording time
         recordAllPendingTimes();
    });

    // Window Blur (User switched to another window/app)
    window.addEventListener('blur', () => {
        console.log("Window lost focus (blur), recording pending times...");
        recordAllPendingTimes();
    });

    // Window Focus (User switched back to this window/app)
    window.addEventListener('focus', () => {
        console.log("Window gained focus (focus), restarting timers...");
        restartTimersOnFocus();
    });
}

// New function to handle restarting timers when page/window becomes active
function restartTimersOnFocus() {
    if (currentViewMode === 'paginate') {
        if (currentPageNumber !== null) {
            // Only reset start time if it was previously cleared (e.g., by blur/hide)
            if (currentPageStartTime === null) {
                 console.log(`Paginate: Restarting timer for page ${currentPageNumber} on focus.`);
                 currentPageStartTime = Date.now();
                 // We don't re-send page_view here, just restart time accumulation
            } else {
                 console.log(`Paginate: Timer for page ${currentPageNumber} was already running.`);
            }
        }
    } else if (currentViewMode === 'scroll') {
        // For scroll mode, the IntersectionObserver handles restarting timers
        // automatically when the element becomes visible again in the viewport.
        // We might force a check if needed, but usually it's not necessary.
        console.log("Scroll: IntersectionObserver will handle timer restarts on focus.");
    }
}

function recordAllPendingTimes() {
    console.log("Recording all pending time events..."); // Added for clarity
    if (currentViewMode === 'scroll') {
        Object.keys(pageTimers).forEach(pageStr => {
            const pageNumber = parseInt(pageStr, 10);
            if (!isNaN(pageNumber)) { // Ensure pageNumber is valid
                recordTimeOnPage(pageNumber); // Records and sends if duration is sufficient
                delete pageTimers[pageNumber]; // Clear the timer regardless
            }
        });
    } else if (currentViewMode === 'paginate') {
        if (currentPageNumber !== null && currentPageStartTime !== null) {
            const durationMs = Date.now() - currentPageStartTime;
             if (durationMs >= MIN_TIME_ON_PAGE_MS) {
                console.log(`Recording final time for page ${currentPageNumber} (${durationMs}ms)`); // Added log
                sendAnalyticsEvent('time_on_page', currentPageNumber, durationMs);
             } else {
                console.log(`Skipping final time_on_page for page ${currentPageNumber}, duration ${durationMs}ms < ${MIN_TIME_ON_PAGE_MS}ms`);
             }
            // Always reset currentPageStartTime after recording/checking
            currentPageStartTime = null;
            console.log(`Cleared currentPageStartTime for page ${currentPageNumber}`); // Added log
        } else {
             console.log("Paginate: No active page timer to record."); // Added log
        }
    }
     console.log("Finished recording pending times."); // Changed log message slightly
} 